import type { ReactNode } from "react";
import { SummaryMetrics, type SummaryMetric } from "@studio";

/**
 * 요약 숫자 카드 (2026-10-21 사용자: 카드를 누르면 그 숫자의 목록 화면으로 간다).
 * 시안 ba7cb9b(요청서 25절)부터 공통 SummaryMetrics 가 클릭(onSelect·actionLabel)을 받으므로 여기서는 이름만 맞춘다.
 * 목적지·권한·첫 필터는 각 화면이 정한다 (onOpen 을 안 넘기면 숫자만).
 */
export type MetricItem = {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  icon?: ReactNode;
  tone?: string;
  /** 누르면 갈 곳. 권한이 없으면 넘기지 않는다 (그 카드는 그냥 숫자). */
  onOpen?: () => void;
  /** 화면 읽기용 이동 설명 (예: "작업 목록에서 보기"). */
  openLabel?: string;
};

const TONES = new Set(["blue", "teal", "amber", "coral", "neutral"]);
const toneOf = (t?: string): SummaryMetric["tone"] => (t === "slate" ? "neutral" : t && TONES.has(t) ? (t as SummaryMetric["tone"]) : undefined);

export function MetricGrid({
  items,
  density = "default",
  label = "요약 정보",
  columns,
}: {
  items: MetricItem[];
  density?: "default" | "compact";
  label?: string;
  columns?: 1 | 2 | 3 | 4;
}) {
  return (
    <SummaryMetrics
      density={density}
      label={label}
      columns={columns}
      items={items.map((i) => ({
        label: i.label,
        value: i.value,
        note: i.note,
        icon: i.icon,
        tone: toneOf(i.tone),
        onSelect: i.onOpen,
        actionLabel: i.onOpen ? `${i.label} — ${i.openLabel ?? "자세히 보기"}` : undefined,
      }))}
    />
  );
}

/** 카드에서 넘어온 첫 필터 값 (?status=failed 등). 화면이 처음 열릴 때 한 번만 읽는다. */
export function urlParam(key: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    return null;
  }
}
